import { X } from "lucide-react";
import { useState, useEffect } from "preact/hooks";
import { GenericForm } from "../Forms/GenericForm";
import Assistant from "./Assistant";
import useMetadataStore from "@/stores/metaDataStore";
import OfferPreviewCard from "./OfferPreviewCard/OfferPreviewCard";
import { useKismetConfig } from "../../contexts/KismetContext";
import { getMonitoringService } from "../../lib/monitoring";
import UMPConfirmation from "./UMPConfirmation";

interface ChatInterfaceProps {
  onClose: () => void;
}

export const ChatInterface = ({ onClose }: ChatInterfaceProps) => {
  const [showForm, setShowForm] = useState(false);
  const [showFullScreenOffer, setShowFullScreenOffer] = useState(false);
  const [showUserProfileScreen, setShowUserProfileScreen] = useState(false);
  const [confirmedUserProfile, setConfirmedUserProfile] = useState(false);
  const { offerMetadata } = useMetadataStore();
  const kismetConfig = useKismetConfig();
  const monitoringService = getMonitoringService();

  // Helper function to check if firstName is valid
  const isRealUserName = (firstName?: string): boolean => {
    return (
      !!firstName &&
      firstName !== "@user" &&
      firstName.toLowerCase() !== "user" &&
      firstName.toLowerCase() !== ""
    );
  };

  useEffect(() => {
    if (!offerMetadata) {
      setShowForm(true);
      setShowUserProfileScreen(false);
      return;
    }

    // If we have a userMarketingProfile with a valid firstName, show the user profile screen first
    if (
      offerMetadata.userMarketingProfile &&
      (isRealUserName(offerMetadata?.user?.firstName) ||
        isRealUserName(offerMetadata.userMarketingProfile.firstName))
    ) {
      setShowUserProfileScreen(true);
      setShowFullScreenOffer(false);
      setShowForm(false);
    } else {
      // No valid userMarketingProfile, go directly to form or offer based on expiration
      setShowUserProfileScreen(false);

      // Determine whether to show form or offer based on expiration
      if (offerMetadata.isExpired) {
        setShowForm(true);
        setShowFullScreenOffer(false);
      } else {
        setShowForm(false);
        setShowFullScreenOffer(true);
      }
    }
  }, [offerMetadata]);

  const handleSaveDeal = () => {
    monitoringService.onPuoClick();
    setShowForm(true);
    setShowFullScreenOffer(false);
  };

  // Function to continue to the actual offer or form based on expiration status
  const handleContinueToOffer = () => {
    // Save the fact that user confirmed their identity with the marketing profile
    setConfirmedUserProfile(true);
    setShowUserProfileScreen(false);

    // Determine whether to show form or offer based on expiration
    if (offerMetadata?.isExpired) {
      setShowForm(true);
      setShowFullScreenOffer(false);
    } else {
      setShowForm(false);
      setShowFullScreenOffer(true);
    }
  };

  // Handle case where user indicates they are not the person
  const handleRejectIdentity = () => {
    console.log("User clicked 'No, that's not me'");
    // Reset the confirmed user profile status
    setConfirmedUserProfile(false);
    // TODO: Handle case where user indicates they are not the person
    // This could involve resetting user data, showing a different form, etc.
  };

  const handleClose = () => {
    console.debug("Closing chat interface");
    onClose();
  };

  // Define form text content based on whether the offer is expired
  const getFormContent = () => {
    if (offerMetadata) {
      if (offerMetadata?.isExpired) {
        return {
          title: "This Offer Has Expired",
          subtitle: "Please enter your information below to get your new offer",
          submitButtonText: "Get Offer",
        };
      } else {
        return {
          title: "Save Your Deal For Later",
          subtitle:
            "Please enter your information below to save your deal for later.",
          submitButtonText: "Submit",
        };
      }
    } else {
      return {
        title: "Sign Up For A One Time Deal",
        subtitle:
          "Please enter your information to receive a unique one time offer.",
        submitButtonText: "Submit",
      };
    }
  };

  return (
    <div className="chat-interface fixed inset-0 md:bottom-0 md:right-0 md:inset-auto z-50 md:p-8 animate-slide-up">
      <div className="kismet-chat-content bg-white w-full h-screen md:h-[700px] md:w-[500px] md:rounded-xl overflow-hidden flex flex-col">
        {/* Black Header */}
        <div className="modal-header bg-black p-6 relative flex-shrink-0">
          {kismetConfig.theme?.content?.logo?.text && (
            <h1 className="modal-header-text text-white text-2xl font-bold text-center">
              {kismetConfig.theme.content.logo.text}
            </h1>
          )}
          {kismetConfig.theme?.content?.logo?.image && (
            <img
              src={kismetConfig.theme.content.logo.image.src}
              alt={kismetConfig.theme.content.logo.image.alt}
              className="h-8 mx-auto"
            />
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="kismet-modal-close absolute right-4 top-4 text-white hover:opacity-75 cursor-pointer pointer-events-auto"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content - flex-grow to fill available space */}
        <div className="flex-grow overflow-hidden">
          {/* User Marketing Profile Confirmation Screen */}
          {showUserProfileScreen &&
            offerMetadata &&
            offerMetadata.userMarketingProfile && (
              <UMPConfirmation
                onConfirm={handleContinueToOffer}
                onReject={handleRejectIdentity}
              />
            )}

          {/* Offer Preview Card */}
          {showFullScreenOffer &&
            !showForm &&
            !showUserProfileScreen &&
            offerMetadata &&
            !offerMetadata.isExpired && (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <OfferPreviewCard
                  headlineText={offerMetadata.headlineText}
                  descriptionText={offerMetadata.descriptionText}
                  imageUrl={
                    offerMetadata.hotel.guestBookingHeroImageSummerBlobFilePath
                  }
                  hotelName={offerMetadata.hotel.name}
                  hotelLogo={offerMetadata.hotel.hotelLogoUrl}
                  discountPercentage={offerMetadata.discountPercentage}
                  expiresAt={offerMetadata.expiresAt}
                  offerCode={offerMetadata.metadata?.offerCode}
                  onSaveDeal={handleSaveDeal}
                />
              </div>
            )}
          {!showFullScreenOffer && !showUserProfileScreen && showForm && (
            <GenericForm
              onClose={handleClose}
              {...getFormContent()}
              confirmedUserProfile
            />
          )}

          {!showFullScreenOffer && !showUserProfileScreen && !showForm && (
            <Assistant />
          )}
        </div>
      </div>
    </div>
  );
};
